import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AdminData } from '../_models/admin';
import { CurrencyData } from '../_models/currency';
import { ApiService } from '../_services/api.service';
import { ChecksService } from '../_services/checks.service';
import { NotificationService } from '../_services/notification.service';

interface TreasurySection {
  slug: string;
  title: string;
  summary: string;
}

interface AccessStep {
  key: string;
  label: string;
  role: string;
  status: 'waiting' | 'current' | 'approved' | 'rejected';
  actor?: string;
  decidedAt?: string;
}

interface AccessRequest {
  ref: string | number;
  staffName: string;
  reason: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  currentStep: number;
  createdAt: string;
  steps: AccessStep[];
}

interface CurrencyPair {
  ref: string | number;
  source: string;
  target: string;
  spread: number;
  enabled: boolean;
  approvalStatus: 'Approved' | 'Pending Approval';
}

interface RateRow {
  ref?: string | number;
  pair: string;
  rate: number;
  spread: number;
  finalRate: number;
  lastUpdated?: string;
}

interface PositionRow {
  ref?: string | number;
  id: string;
  pair: string;
  amount: number;
  openedDate: string;
  pnl: number;
  status: 'open' | 'settled';
}

interface TradeRow {
  ref: string | number;
  rail: string;
  pair: string;
  amount: number;
  status: string;
  timestamp: string;
}

interface BalanceRow {
  ref?: string | number;
  currency: string;
  balance: number;
}

interface ClosingBalanceRow extends BalanceRow {
  closingDate: string;
}

interface BalanceTransaction {
  ref: string | number;
  currency: string;
  type: string;
  amount: number;
  timestamp: string;
}

@Component({
  selector: 'app-treasury',
  templateUrl: './treasury.component.html',
  styleUrl: './treasury.component.css'
})
export class TreasuryComponent implements OnInit, OnDestroy {
  sections: TreasurySection[] = [
    { slug: 'currency-swap-dashboard', title: 'Currency swap dashboard', summary: 'Preview, confirm, and record controlled currency swaps.' },
    { slug: 'multi-rail-trade-records', title: 'Multi-rail trade records', summary: 'Review trades across payment rails, currency pairs, statuses, and dates.' },
    { slug: 'digital-asset-rates', title: 'Digital asset rates', summary: 'View current pair rates and rate-feed health.' },
    { slug: 'open-positions', title: 'Open positions', summary: 'Track open positions and settle them with confirmation.' },
    { slug: 'currency-pair-config', title: 'Currency pair config', summary: 'Configure pairs, spreads, enablement, and approval state.' },
    { slug: 'balance-dashboard', title: 'Balance dashboard', summary: 'Move through summary, currency, and transaction balance layers.' },
    { slug: 'closing-balance-daily', title: 'Closing balance daily', summary: 'Review daily closing balance snapshots and export them.' },
    { slug: 'trade-exports', title: 'Trade exports (PDF/Excel)', summary: 'Export treasury trade data for audit and reconciliation.' },
  ];

  activeSection: TreasurySection = this.sections[0];
  private routeSubscription: Subscription;
  currentUser: AdminData;

  activeCurrencies: CurrencyData[] = [];
  rails = ['Bank', 'Stablecoin', 'Mobile Money', 'Card', 'Internal Ledger'];
  statuses = ['Pending', 'Processing', 'Completed', 'Failed', 'Settled'];

  accessRequest: AccessRequest = this.createAccessRequest('Awaiting treasury view-only access for operations support.');
  pairList: CurrencyPair[] = [];
  rateList: RateRow[] = [];
  positions: PositionRow[] = [];
  trades: TradeRow[] = [];
  balances: BalanceRow[] = [];
  balanceTransactions: BalanceTransaction[] = [];
  closingBalanceRows: ClosingBalanceRow[] = [];

  rateFeedAvailable = true;
  lastRateUpdate = new Date();
  selectedBalanceCurrency = '';
  balanceLayer: 'summary' | 'currency' | 'transactions' = 'summary';
  swapPreview: { rate: number; result: number; pair: string } = null;
  exportReady = false;

  accessForm = this.fb.group({ reason: ['', Validators.required] });
  pairForm = this.fb.group({ source: ['NGN', Validators.required], target: ['USD', Validators.required], spread: [1, Validators.required] });
  rateForm = this.fb.group({ pair: ['', Validators.required], rate: [0, Validators.required] });
  positionForm = this.fb.group({ positionId: [''], pair: ['', Validators.required], amount: [0, Validators.required], openedDate: [this.today(), Validators.required], pnl: [0], status: ['open'] });
  tradeForm = this.fb.group({ tradeId: [''], rail: ['Bank', Validators.required], pair: ['', Validators.required], amount: [0, Validators.required], status: ['Pending', Validators.required], timestamp: [''] });
  balanceForm = this.fb.group({ currency: ['NGN', Validators.required], balance: [0, Validators.required] });
  balanceTransactionForm = this.fb.group({ currency: ['NGN', Validators.required], type: ['Credit', Validators.required], amount: [0, Validators.required], timestamp: [''] });
  closingEntryForm = this.fb.group({ closingDate: [this.today(), Validators.required], currency: ['NGN', Validators.required], balance: [0, Validators.required] });
  swapForm = this.fb.group({ source: ['NGN', Validators.required], target: ['USD', Validators.required], amount: [0, Validators.required] });
  tradeFilterForm = this.fb.group({ rail: [''], pair: [''], status: [''], dateFrom: [''], dateTo: [''] });
  closingBalanceForm = this.fb.group({ date: [this.today()] });
  exportForm = this.fb.group({ dateFrom: [this.monthStart()], dateTo: [this.today()], format: ['Excel'] });

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private apiService: ApiService,
    public checkService: ChecksService,
    private notifyService: NotificationService,
  ) {
    this.checkService.checkSession();
  }

  ngOnInit(): void {
    this.currentUser = this.checkService.getAdminData();
    this.loadActiveCurrencies();
    this.loadTreasuryData();
    this.routeSubscription = this.route.paramMap.subscribe(params => {
      const sectionSlug = params.get('section') || this.sections[0].slug;
      this.activeSection = this.sections.find(section => section.slug === sectionSlug) || this.sections[0];
    });
  }

  loadActiveCurrencies(): void {
    this.apiService.getActiveCurrencies().subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.activeCurrencies = data.data || [];
      }
    });
  }

  currencyCodes(): string[] {
    return this.activeCurrencies.map(currency => currency.code);
  }

  loadTreasuryData(): void {
    this.apiService.getTreasuryData({ ...this.tradeFilterForm.value, date: this.closingBalanceForm.value.date }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        const payload = data.data || {};
        if (payload.accessRequest) {
          this.accessRequest = payload.accessRequest;
        }
        this.pairList = (payload.pairs || []).map(row => ({
          ref: row.ref,
          source: row.source,
          target: row.target,
          spread: Number(row.spread || 0),
          enabled: Number(row.enabled) === 1 || row.enabled === true,
          approvalStatus: row.approvalStatus || 'Pending Approval',
        }));
        this.rateList = (payload.rates || []).map(row => ({
          ref: row.ref,
          pair: row.pair,
          rate: Number(row.rate || 0),
          spread: Number(row.spread || 0),
          finalRate: Number(row.finalRate || 0),
          lastUpdated: row.lastUpdated,
        }));
        this.positions = (payload.positions || []).map(row => ({
          ref: row.ref,
          id: row.id,
          pair: row.pair,
          amount: Number(row.amount || 0),
          openedDate: row.openedDate,
          pnl: Number(row.pnl || 0),
          status: row.status,
        }));
        this.trades = (payload.trades || []).map(row => ({
          ref: row.ref,
          rail: row.rail,
          pair: row.pair,
          amount: Number(row.amount || 0),
          status: row.status,
          timestamp: row.timestamp,
        }));
        this.balances = (payload.balances || []).map(row => ({ ref: row.ref, currency: row.currency, balance: Number(row.balance || 0) }));
        this.balanceTransactions = (payload.balanceTransactions || []).map(row => ({ ref: row.ref, currency: row.currency, type: row.type, amount: Number(row.amount || 0), timestamp: row.timestamp }));
        this.closingBalanceRows = (payload.closingBalances || []).map(row => ({ ref: row.ref, currency: row.currency, balance: Number(row.balance || 0), closingDate: row.closingDate }));
        this.rateFeedAvailable = payload.rateFeedAvailable !== false;
        this.lastRateUpdate = payload.lastRateUpdate ? new Date(payload.lastRateUpdate) : new Date();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  createAccessRequest(reason: string): AccessRequest {
    const steps: AccessStep[] = [
      { key: 'head_hr', label: 'Head of HR', role: 'Head of HR', status: 'current' },
      { key: 'compliance', label: 'Compliance', role: 'Compliance', status: 'waiting' },
      { key: 'audit', label: 'Internal Audit', role: 'Internal Audit', status: 'waiting' },
      { key: 'coo', label: 'COO', role: 'COO', status: 'waiting' },
      { key: 'ceo', label: 'CEO', role: 'CEO', status: 'waiting' },
    ];
    return { ref: 'TAR-0001', staffName: this.currentUser?.nickName || 'Current staff', reason, status: 'pending', currentStep: 0, createdAt: this.today(), steps };
  }

  submitAccessRequest(): void {
    if (this.accessForm.invalid) {
      this.accessForm.markAllAsTouched();
      return;
    }
    this.apiService.requestTreasuryAccess(this.accessForm.value).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.accessRequest = data.data;
        this.notifyService.showSuccess('Treasury access request submitted', 'Treasury');
        this.accessForm.reset();
      }
    });
  }

  canActOnCurrentStep(): boolean {
    const current = this.accessRequest.steps[this.accessRequest.currentStep];
    if (!current || this.accessRequest.status !== 'pending') {
      return false;
    }
    const roleText = `${this.currentUser?.rights?.slug || ''} ${this.currentUser?.rights?.adminType || ''}`.toLowerCase();
    return roleText.includes(current.role.toLowerCase().replace('head of ', '')) || this.currentUser?.rights?.super === true;
  }

  approveAccess(): void {
    const current = this.accessRequest.steps[this.accessRequest.currentStep];
    if (!current) {
      return;
    }
    this.apiService.decideTreasuryAccess({ ref: this.accessRequest.ref, decision: 'approve' }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.accessRequest = data.data;
        this.notifyService.showSuccess('Treasury access step approved', 'Treasury');
      }
    });
  }

  rejectAccess(): void {
    const current = this.accessRequest.steps[this.accessRequest.currentStep];
    if (!current) {
      return;
    }
    this.apiService.decideTreasuryAccess({ ref: this.accessRequest.ref, decision: 'reject' }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.accessRequest = data.data;
      }
    });
  }

  hasTreasuryViewAccess(): boolean {
    return this.accessRequest.status === 'approved' || this.checkService.canModify('treasury');
  }

  addPair(): void {
    if (this.pairForm.invalid || this.pairForm.value.source === this.pairForm.value.target) {
      this.notifyService.showError('Select two different currencies', 'Treasury');
      return;
    }
    this.apiService.saveTreasuryPair(this.pairForm.value).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.notifyService.showSuccess('Currency pair submitted for approval', 'Treasury');
        this.loadTreasuryData();
      }
    });
  }

  saveRate(): void {
    if (this.rateForm.invalid || Number(this.rateForm.value.rate || 0) <= 0) {
      this.rateForm.markAllAsTouched();
      return;
    }
    this.apiService.saveTreasuryRate(this.rateForm.value).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.notifyService.showSuccess('Rate saved', 'Treasury');
        this.rateForm.patchValue({ rate: 0 });
        this.loadTreasuryData();
      }
    });
  }

  savePosition(): void {
    if (this.positionForm.invalid || Number(this.positionForm.value.amount || 0) <= 0) {
      this.positionForm.markAllAsTouched();
      return;
    }
    this.apiService.saveTreasuryPosition(this.positionForm.value).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.notifyService.showSuccess('Position saved', 'Treasury');
        this.positionForm.patchValue({ positionId: '', amount: 0, pnl: 0, status: 'open' });
        this.loadTreasuryData();
      }
    });
  }

  saveTrade(): void {
    if (this.tradeForm.invalid || Number(this.tradeForm.value.amount || 0) <= 0) {
      this.tradeForm.markAllAsTouched();
      return;
    }
    this.apiService.saveTreasuryTrade(this.tradeForm.value).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.notifyService.showSuccess('Trade saved', 'Treasury');
        this.tradeForm.patchValue({ tradeId: '', amount: 0, status: 'Pending', timestamp: '' });
        this.loadTreasuryData();
      }
    });
  }

  saveBalance(): void {
    if (this.balanceForm.invalid) {
      this.balanceForm.markAllAsTouched();
      return;
    }
    this.apiService.saveTreasuryBalance(this.balanceForm.value).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.notifyService.showSuccess('Balance saved', 'Treasury');
        this.loadTreasuryData();
      }
    });
  }

  saveBalanceTransaction(): void {
    if (this.balanceTransactionForm.invalid || Number(this.balanceTransactionForm.value.amount || 0) === 0) {
      this.balanceTransactionForm.markAllAsTouched();
      return;
    }
    this.apiService.saveTreasuryBalanceTransaction(this.balanceTransactionForm.value).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.notifyService.showSuccess('Balance transaction saved', 'Treasury');
        this.balanceTransactionForm.patchValue({ amount: 0, timestamp: '' });
        this.loadTreasuryData();
      }
    });
  }

  saveClosingBalance(): void {
    if (this.closingEntryForm.invalid) {
      this.closingEntryForm.markAllAsTouched();
      return;
    }
    this.apiService.saveTreasuryClosingBalance(this.closingEntryForm.value).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.notifyService.showSuccess('Closing balance saved', 'Treasury');
        this.loadTreasuryData();
      }
    });
  }

  togglePair(pair: CurrencyPair): void {
    this.apiService.toggleTreasuryPair({ ref: pair.ref }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.loadTreasuryData();
      }
    });
  }

  rateRows(): RateRow[] {
    if (!this.rateFeedAvailable) {
      return [];
    }
    return this.rateList;
  }

  filteredTrades(): TradeRow[] {
    const filter = this.tradeFilterForm.value;
    return this.trades.filter(trade => {
      const tradeDate = trade.timestamp.slice(0, 10);
      return (!filter.rail || trade.rail === filter.rail)
        && (!filter.pair || trade.pair === filter.pair)
        && (!filter.status || trade.status === filter.status)
        && (!filter.dateFrom || tradeDate >= filter.dateFrom)
        && (!filter.dateTo || tradeDate <= filter.dateTo);
    });
  }

  closePosition(position: PositionRow): void {
    if (!confirm(`Settle ${position.id}?`)) {
      return;
    }
    this.apiService.settleTreasuryPosition({ ref: position['ref'] || position.id }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.notifyService.showSuccess('Position settled', 'Treasury');
        this.loadTreasuryData();
      }
    });
  }

  previewSwap(): void {
    if (this.swapForm.invalid || Number(this.swapForm.value.amount || 0) <= 0) {
      this.swapForm.markAllAsTouched();
      return;
    }
    const pair = `${this.swapForm.value.source}/${this.swapForm.value.target}`;
    const rate = this.rateRows().find(row => row.pair === pair)?.finalRate || 0;
    if (rate <= 0) {
      this.notifyService.showError('Enter a current rate for this currency pair first', 'Treasury');
      return;
    }
    this.swapPreview = { pair, rate, result: Number(this.swapForm.value.amount || 0) * rate };
  }

  confirmSwap(): void {
    if (!this.swapPreview || !confirm(`Record swap ${this.swapPreview.pair}?`)) {
      return;
    }
    this.apiService.recordTreasurySwap({ pair: this.swapPreview.pair, amount: Number(this.swapForm.value.amount || 0) }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.notifyService.showSuccess('Swap recorded', 'Treasury');
        this.swapPreview = null;
        this.loadTreasuryData();
      }
    });
  }

  openCurrencyLayer(currency: string): void {
    this.selectedBalanceCurrency = currency;
    this.balanceLayer = 'transactions';
  }

  totalBalance(): number {
    return this.balances.reduce((sum, row) => sum + Number(row.balance || 0), 0);
  }

  selectedTransactions(): BalanceTransaction[] {
    return this.balanceTransactions.filter(row => row.currency === this.selectedBalanceCurrency);
  }

  closingBalances(): ClosingBalanceRow[] {
    const selectedDate = this.closingBalanceForm.value.date;
    return this.closingBalanceRows.filter(row => row.closingDate === selectedDate);
  }

  exportTreasuryData(scope: string, format: string = 'Excel'): void {
    const rows = scope === 'closing'
      ? this.closingBalances().map(row => [row.currency, row.balance])
      : this.filteredTrades().map(row => [row.ref, row.rail, row.pair, row.amount, row.status, row.timestamp]);
    const title = scope === 'closing' ? 'Treasury Closing Balances' : 'Treasury Trade Export';
    const html = `<html><body><h1>${title}</h1><table border="1">${rows.map(row => '<tr>'+row.map(cell => '<td>'+cell+'</td>').join('')+'</tr>').join('')}</table></body></html>`;
    if (format === 'PDF') {
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
        win.print();
      }
      return;
    }
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${scope}-export.xls`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  pairOptions(): string[] {
    return this.pairList.map(pair => `${pair.source}/${pair.target}`);
  }

  today(): string {
    return new Date().toISOString().slice(0, 10);
  }

  monthStart(): string {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().slice(0, 10);
  }
}
